import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download, Mail } from "lucide-react";
import { CONTACT, OCT_DECK, IMAGES } from "./data";
import { EASE, scrollToId } from "./shared";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const METRICS = [
  { value: "$250K", label: "Bridge ask — SAFE or simple convertible note", testid: "hero-metric-ask" },
  { value: "$0", label: "Revenue to date — stated plainly", testid: "hero-metric-zero" },
  { value: "$1,000", label: "Founding pilot offer", testid: "hero-metric-offer" },
  { value: "4–6 mo", label: "Runway this round funds", testid: "hero-metric-runway" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="h-[115%] w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E17]/75 via-[#0A0E17]/85 to-[#0A0E17]" />
        <div className="noise-overlay absolute inset-0" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-5 pt-36 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="hero-overline"
          className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#FF5500]"
        >
          WorkSafeAgent LLC · Abilene, TX · Investor Relations · October 2026
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="font-display mt-6 max-w-4xl text-4xl font-semibold uppercase leading-[1.02] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.25}>A $250K bridge.</MaskedLine>
          <MaskedLine delay={0.4}>
            Founding pilots. <span className="text-[#FF5500]">Zero spin.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          data-testid="hero-subheading"
          className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          $250K bridge (SAFE or simple convertible note, terms set with counsel) to run founding
          pilots on active jobsites — followed by a $4.5M round after traction. That round is not
          the current ask.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-cta-deck"
            href={OCT_DECK.href}
            download
            className="group flex items-center gap-2 rounded-full bg-[#FF5500] px-6 py-3 font-mono text-sm font-semibold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-[#E04B00]"
          >
            <Download className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            Download the October 2026 deck
          </a>
          <a
            data-testid="hero-cta-email"
            href={`mailto:${CONTACT.email}?subject=$250K%20Bridge%20—%20Investor%20Inquiry`}
            className="flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3 font-mono text-sm font-semibold uppercase tracking-widest text-slate-200 transition-colors duration-200 hover:border-[#FF5500] hover:text-[#FF5500]"
          >
            <Mail className="h-4 w-4" />
            Email the founder
          </a>
          <button
            data-testid="hero-cta-traction"
            onClick={() => scrollToId("traction")}
            className="flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-slate-400 transition-colors duration-200 hover:text-slate-100"
          >
            See the honest numbers
            <ArrowDown className="h-4 w-4" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 grid grid-cols-2 border-t border-slate-800 md:grid-cols-4"
        >
          {METRICS.map((m) => (
            <div
              key={m.testid}
              data-testid={m.testid}
              className="border-r border-slate-800 px-4 py-6 last:border-r-0 md:px-6 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r"
            >
              <p className="font-display text-3xl font-semibold text-slate-100 sm:text-4xl">{m.value}</p>
              <p className="mt-1 font-mono text-[11px] uppercase leading-relaxed tracking-wider text-slate-500">
                {m.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="h-12" />
    </section>
  );
}
